import { BarcodeOutlined, SearchOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import { memo, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

const SearchInput = ({
    value: controlledValue,
    placeholder = 'Search...',
    delay = 500,
    onSearch,
    size = 'large',
    className,
    isScan = false,
    ...rest
}) => {
    const [value, setValue] = useState(controlledValue ?? '');
    const inputRef = useRef(null);

    const scan = useRef({
        startedAt: 0,
        lastKey: 0,
        intervals: [],
        chars: 0,
    });

    const SCAN_INTERVAL = 35;
    const MIN_SCAN_LENGTH = 4;

    const resetScan = () => {
        scan.current = {
            startedAt: 0,
            lastKey: 0,
            intervals: [],
            chars: 0,
        };
    };

    useEffect(() => {
        if (!isScan) return;
        const timer = setTimeout(() => {
            inputRef.current?.focus();
        }, 300);
        return () => clearTimeout(timer);
    }, [isScan]);

    useEffect(() => {
        setValue(controlledValue ?? '');
    }, [controlledValue]);

    const debouncedSearch = useDebouncedCallback((val, mode) => {
        onSearch?.(val.trim(), { mode });
    }, delay);

    const handleKeyDown = (e) => {
        const now = performance.now();

        if (e.key.length === 1) {
            if (!scan.current.startedAt) {
                scan.current.startedAt = now;
            }

            if (scan.current.lastKey) {
                scan.current.intervals.push(now - scan.current.lastKey);
            }

            scan.current.lastKey = now;
            scan.current.chars++;
        }

        if (e.key === 'Enter') {
            const avg =
                scan.current.intervals.reduce((a, b) => a + b, 0) /
                (scan.current.intervals.length || 1);

            const mode =
                scan.current.chars >= MIN_SCAN_LENGTH && avg < SCAN_INTERVAL
                    ? 'scan'
                    : 'manual';

            debouncedSearch.cancel(); 

            setTimeout(() => {
                const finalValue = inputRef.current?.input?.value || value;
                onSearch?.(finalValue.trim(), { mode });
                resetScan();
            }, 150);
        }
    };

    const handleChange = (e) => {
        const val = e.target.value;
        setValue(val);
        
        if (!val) {
            resetScan();
        }

        debouncedSearch(val, 'manual');
    };

    return (
        <Input
            ref={inputRef}
            className={className ?? 'w-full lg:max-w-20rem'}
            size={size}
            placeholder={placeholder}
            prefix={
                isScan ? (
                    <BarcodeOutlined style={{ fontSize: 24 }} />
                ) : (
                    <SearchOutlined />
                )
            }
            value={value}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            allowClear
            {...rest}
        />
    );
};

export default memo(SearchInput);